/**
 * @license
 * SPDX-License-Identifier: Apache-2.5
 */

import React, { useState, useEffect, useMemo } from 'react';
import { User, OrdenProduccion, DisposicionProducto, Company, BackupRecord, ActivityLog } from './types';
import { 
  INITIAL_USERS, 
  INITIAL_ORDENES, 
  INITIAL_DISPOSICIONES,
  INITIAL_BACKUPS,
  generatePredefinedSignature
} from './data/mockData';
import Header from './components/Header';
import AuthScreen from './components/AuthScreen';
import RegistroOrdenes from './components/RegistroOrdenes';
import DisposicionProductos from './components/DisposicionProductos';
import DashboardProductividad from './components/DashboardProductividad';
import GestionUsuarios from './components/GestionUsuarios';
import SuscripcionPortal from './components/SuscripcionPortal';
import SaaSAdminPanel from './components/SaaSAdminPanel';
import BackupManagerModal from './components/BackupManagerModal';
import GestorResguardoModule from './components/GestorResguardoModule';
import RenewalWarningModal from './components/RenewalWarningModal';

import { 
  getSavedCompanies, 
  getDaysRemaining, 
  addAuditLog, 
  getSavedPayments,
  DEFAULT_COMPANY,
  getSavedLogs,
  isCompanySubscriptionExpired
} from './utils/saasUtils';

import { 
  setupRealtimeSync, 
  syncRegistry, 
  uploadItemToFirestore, 
  deleteItemFromFirestore,
  forceSyncFromFirestore,
  cleanupAllRealtimeSync
} from './utils/firebaseSync';
import { registerAuthListener, logoutFirebaseAuth, AuthState } from './utils/firebaseAuth';
import { getOrCreateAuthoritativeUserAuth } from './utils/authAuthority';
import { computeSha256, verifyBackupIntegrity } from './utils/cryptoUtils';

import { 
  FileText, 
  ClipboardCheck, 
  BarChart3, 
  Clock, 
  AlertCircle, 
  AlertTriangle,
  Users, 
  Settings, 
  CreditCard, 
  Sparkles,
  Lock,
  ArrowRight,
  ShieldCheck,
  Building,
  Mail,
  Database,
  Upload,
  Download,
  RefreshCw,
  Boxes,
  History
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

type MenuTab = 
  | 'registro_ordenes' 
  | 'disposicion_productos' 
  | 'dashboard_productividad' 
  | 'gestion_usuarios' 
  | 'gestor_resguardo' 
  | 'saas_admin' 
  | 'mi_suscripcion';

export default function App() {
  // Load initial states with localStorage checks
  const [users, setUsers] = useState<User[]>(() => {
    const saved = localStorage.getItem('insumedic_users');
    let loadedUsers: User[] = saved ? JSON.parse(saved) : [];
    
    const ALLOWED_USERNAMES = ['JUAN.VALENCIA', 'LAURA.CARRERO', 'MARIA.CECHAGUA', 'NATIVIDAD.CUELLAR', 'ADM.SISTEMAS', 'ERNESTO.TRILLERAS'];
    
    // Default or map companyId correctly to company-insumedic for legacy records
    loadedUsers = loadedUsers.map(u => ({
      ...u,
      companyId: u.companyId || 'company-insumedic'
    }));

    // If some of the 6 are missing from loadedUsers, pull them from INITIAL_USERS
    ALLOWED_USERNAMES.forEach(username => {
      const exists = loadedUsers.some(u => u.username.toUpperCase() === username);
      if (!exists) {
        const fallback = INITIAL_USERS.find(u => u.username.toUpperCase() === username);
        if (fallback) {
          loadedUsers.push({ ...fallback, companyId: 'company-insumedic' });
        }
      }
    });

    // Make sure juan.valencia is set correctly
    const valenciaIdx = loadedUsers.findIndex(u => u.username.toUpperCase() === 'JUAN.VALENCIA');
    if (valenciaIdx !== -1) {
      loadedUsers[valenciaIdx] = {
        ...loadedUsers[valenciaIdx],
        username: 'JUAN.VALENCIA',
        nombreCompleto: 'Juan Pablo Valencia Berrio',
        cedula: '16078935',
        password: 'Jvalencia4@',
        correo: 'qualityprocesosyservicios@gmail.com',
        esAdmin: true,
        estado: 'ACTIVO',
        cargo: 'Administrador del Sistema',
        companyId: 'company-insumedic'
      };
    }

    return loadedUsers;
  });

  const [ordenes, setOrdenes] = useState<OrdenProduccion[]>(() => {
    const saved = localStorage.getItem('insumedic_ordenes_v3');
    let loaded: OrdenProduccion[] = [];
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          loaded = parsed;
        }
      } catch (e) {}
    }
    if (loaded.length === 0) {
      loaded = INITIAL_ORDENES;
    }
    // Correct specific orders on load
    const isTargetOP = (num: string) => {
      if (!num) return false;
      return num === 'OP-14072026-1' || num === 'OP-14072026-2' || num === 'OP-140722026-1' || num === 'OP-140722026-2' || num.includes('14072');
    };

    const corrected = loaded.map((o) => {
      let updated = { ...o };
      if (isTargetOP(o.numeroOrden)) {
        updated.fechaRegistro = '2026-07-14T12:00:00.000Z';
        updated.tamanoLote = 100;
        updated.fechaInicioReempaque = '2026-07-14T12:30:00';
        updated.fechaFinReempaque = '2026-07-14T17:35:00';
      }
      if (!updated.elaboradoPor || updated.elaboradoPor === 'Administrador' || updated.elaboradoPor === 'Auxiliar/Operario' || updated.elaboradoPor === 'ADMINISTRADOR DEL SISTEMA') {
        updated.elaboradoPor = 'LAURA PATRICIA CARRERO PERDOMO';
      }
      return updated;
    });

    const hasOP2 = corrected.some(o => o.numeroOrden === 'OP-14072026-2');
    if (!hasOP2) {
      const op1 = corrected.find(o => isTargetOP(o.numeroOrden));
      if (op1) {
        corrected.push({
          id: `${op1.id}-2`,
          numeroOrden: 'OP-14072026-2',
          loteRP: 'RP-2',
          loteFabricante: op1.loteFabricante || 'LOT-14072026-2',
          codigoMedicamento: op1.codigoMedicamento || 'MED-TIZ-100',
          nombreGenerico: op1.nombreGenerico || 'TIZANIDINA',
          nombreComercial: op1.nombreComercial || 'SIRDALUD',
          concentracion: op1.concentracion || '2 MG',
          formaFarmaceutica: op1.formaFarmaceutica || 'TABLETA',
          laboratorioFabricante: op1.laboratorioFabricante || 'NOVARTIS',
          fechaVencimiento: op1.fechaVencimiento || '2028-07-14',
          registroSanitario: op1.registroSanitario || 'INVIMA 2018M-0001234',
          cum: op1.cum || '19901234-1',
          tamanoLote: 100,
          manejoEspecial: op1.manejoEspecial || 'NO',
          estadoOrden: 'Cerrada',
          elaboradoPor: 'LAURA PATRICIA CARRERO PERDOMO',
          verificadoPor: 'MARIA ANGELICA CECHAGUA',
          fechaRegistro: '2026-07-14T12:00:00.000Z',
          fechaInicioReempaque: '2026-07-14T12:30:00',
          fechaFinReempaque: '2026-07-14T17:35:00',
          companyId: op1.companyId || 'company-insumedic'
        });
      }
    }

    INITIAL_ORDENES.forEach((initOrd) => {
      if (!corrected.some(o => o.id === initOrd.id)) {
        corrected.unshift({ ...initOrd });
      }
    });

    localStorage.setItem('insumedic_ordenes_v3', JSON.stringify(corrected));
    return corrected;
  });

  const [disposiciones, setDisposiciones] = useState<DisposicionProducto[]>(() => {
    const saved = localStorage.getItem('insumedic_disposiciones_v3');
    let loaded: DisposicionProducto[] = [];
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          loaded = parsed;
        }
      } catch (e) {}
    }
    if (loaded.length === 0) {
      loaded = INITIAL_DISPOSICIONES;
    }
    const isTargetOP = (num: string) => {
      if (!num) return false;
      return num === 'OP-14072026-1' || num === 'OP-14072026-2' || num === 'OP-140722026-1' || num === 'OP-140722026-2' || num.includes('14072');
    };
    const correctedDisp = loaded.map((d) => {
      let updated = { ...d };
      if (isTargetOP(d.numeroOrden)) {
        updated.fechaDisposicion = '2026-07-14T17:35:00';
      }
      return updated;
    });

    const hasDisp2 = correctedDisp.some(d => d.numeroOrden === 'OP-14072026-2');
    if (!hasDisp2) {
      const disp1 = correctedDisp.find(d => isTargetOP(d.numeroOrden));
      if (disp1) {
        correctedDisp.push({
          id: '7',
          ordenId: `${disp1.ordenId}-2`,
          numeroOrden: 'OP-14072026-2',
          nombreMedicamento: disp1.nombreMedicamento || 'TIZANIDINA (SIRDALUD)',
          estadoDisposicion: 'Aprobado',
          cantidadDispuesta: 100,
          observaciones: 'Acondicionamiento físico correcto del lote RP-2. Verificado cumple estándar asistencial.',
          responsable: 'MARIA ANGELICA CECHAGUA',
          fechaDisposicion: '2026-07-14T17:35:00',
          companyId: disp1.companyId || 'company-insumedic'
        });
      }
    }

    // Guarantee baseline initial dispositions (including Tramadol rejected disposition id: '2') are always preserved
    INITIAL_DISPOSICIONES.forEach((initDisp) => {
      if (!correctedDisp.some(d => d.id === initDisp.id)) {
        correctedDisp.unshift({ ...initDisp });
      }
    });

    localStorage.setItem('insumedic_disposiciones_v3', JSON.stringify(correctedDisp));
    return correctedDisp;
  });

  const [backups, setBackups] = useState<BackupRecord[]>(() => {
    const saved = localStorage.getItem('insumedic_backups_v3');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch (e) {}
    }
    return INITIAL_BACKUPS as BackupRecord[];
  });

  const [showBackupModal, setShowBackupModal] = useState(false);
  const [isUsersSyncComplete, setIsUsersSyncComplete] = useState(false);
  const [isCloudSyncing, setIsCloudSyncing] = useState(false);
  const [cloudSyncStatus, setCloudSyncStatus] = useState<'success' | 'error' | 'syncing' | 'idle'>('idle');
  const [authLifecycleState, setAuthLifecycleState] = useState<AuthState>('AUTH_LOADING');
  const [ssotReady, setSsotReady] = useState(false);

  // Secure initialization of currentUser (UI cache only, validated strictly on load & auth state changes)
  const [currentUser, setCurrentUser] = useState<User | null>(() => {
    try {
      const sessionSaved = sessionStorage.getItem('insumedic_current_user') || localStorage.getItem('insumedic_current_user');
      if (sessionSaved) {
        const parsed = JSON.parse(sessionSaved) as User;
        // Verify against INITIAL_USERS / known identities to prevent unauthenticated privilege spoofing
        const legitimateUser = INITIAL_USERS.find(
          u => u.username.toLowerCase() === parsed.username?.toLowerCase() || u.id === parsed.id
        );
        if (legitimateUser) {
          // Return legitimate attributes, discarding any tampered esAdmin / companyId from localStorage
          return {
            ...legitimateUser,
            firebaseUid: parsed.firebaseUid,
            authProvider: parsed.authProvider || 'legacy'
          };
        }
      }
    } catch (e) {
      console.warn('Session cache parsing failed:', e);
    }
    return null;
  });

  useEffect(() => {
    document.documentElement.classList.remove('dark');
    localStorage.removeItem('insumedic_theme');
  }, []);

  const [activeTab, setActiveTab] = useState<MenuTab>(() => {
    const sessionSaved = sessionStorage.getItem('insumedic_current_user') || localStorage.getItem('insumedic_current_user');
    if (sessionSaved) {
      try {
        const u = JSON.parse(sessionSaved) as User;
        if (u && u.username) {
          if (u.username.toUpperCase() === 'LAURA.CARRERO') {
            return 'disposicion_productos';
          }
          if (u.username.toLowerCase() === 'administrador' || u.username.toLowerCase() === 'juan.valencia' || u.username === 'admin') {
            return 'registro_ordenes';
          }
          if (u.modulosPermitidos && u.modulosPermitidos.length > 0) {
            return u.modulosPermitidos[0] as MenuTab;
          }
        }
      } catch (e) {
        console.warn('Error reading current user session for tab routing:', e);
      }
    }
    return 'registro_ordenes';
  });

  // SaaS Tenant state
  const [sessionActiveCompany, setSessionActiveCompany] = useState<Company | null>(null);

  // Inactivity notification state (15 min timeout)
  const [inactivityNotice, setInactivityNotice] = useState<string | null>(null);

  // 15-Minute Inactivity Auto-Logout Effect (900,000 ms)
  useEffect(() => {
    if (!currentUser) return;

    const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutos = 900.000 ms
    let timerId: NodeJS.Timeout;

    const resetInactivityTimer = () => {
      if (timerId) clearTimeout(timerId);
      timerId = setTimeout(() => {
        addAuditLog(
          currentUser.nombreCompleto,
          'Seguridad',
          'Cierre Inactividad',
          'Sesión cerrada automáticamente por inactividad de 15 minutos (Seguridad RDC / INVIMA / Ciberseguridad)',
          currentUser.companyId,
          sessionActiveCompany?.nombreEmpresa
        );
        setCurrentUser(null);
        setSessionActiveCompany(null);
        setInactivityNotice('Su sesión fue cerrada automáticamente tras 15 minutos de inactividad por políticas de ciberseguridad.');
      }, INACTIVITY_TIMEOUT_MS);
    };

    const userEvents = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    userEvents.forEach((evt) => window.addEventListener(evt, resetInactivityTimer, { passive: true }));

    resetInactivityTimer();

    return () => {
      if (timerId) clearTimeout(timerId);
      userEvents.forEach((evt) => window.removeEventListener(evt, resetInactivityTimer));
    };
  }, [currentUser?.id, sessionActiveCompany?.nombreEmpresa]);

  // Renewal warning modal state
  const [showRenewalModal, setShowRenewalModal] = useState<boolean>(false);
  const [renewalCompanyData, setRenewalCompanyData] = useState<{
    fechaVencimiento: string;
    diasRestantes: number;
    nombreEmpresa: string;
  } | null>(null);

  // Sync session active company details dynamically on load or update
  const handleReloadSaaSSession = () => {
    if (currentUser?.companyId) {
      const list = getSavedCompanies();
      const match = list.find(c => c.id === currentUser.companyId);
      if (match) {
        setSessionActiveCompany(match);
      }
    } else {
      setSessionActiveCompany(null);
    }
  };

  useEffect(() => {
    handleReloadSaaSSession();
  }, [currentUser]);

  // Check subscription expiration upon login/load and auto-show modal if <= 5 days
  useEffect(() => {
    if (!currentUser) {
      setShowRenewalModal(false);
      setRenewalCompanyData(null);
      return;
    }

    const companiesList = getSavedCompanies();
    const company = companiesList.find(c => c.id === (currentUser.companyId || 'company-insumedic')) || companiesList[0];

    if (!company || !company.fechaVencimiento) return;

    const isVit = company.esAcreditacionVitalicia === true || (company.fechaVencimiento ? new Date(company.fechaVencimiento).getFullYear() >= 2099 : false);

    if (!isVit) {
      const now = new Date();
      const expDate = new Date(company.fechaVencimiento);
      const diffMs = expDate.getTime() - now.getTime();
      const daysRemaining = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

      if (daysRemaining <= 5 && daysRemaining >= -30) {
        const dismissKey = `dismissed_renewal_${company.id}_${company.fechaVencimiento.slice(0, 10)}`;
        const isDismissed = sessionStorage.getItem(dismissKey) === 'true';

        setRenewalCompanyData({
          fechaVencimiento: company.fechaVencimiento,
          diasRestantes: Math.max(0, daysRemaining),
          nombreEmpresa: company.nombreEmpresa
        });

        if (!isDismissed) {
          setShowRenewalModal(true);
        }
      } else {
        setRenewalCompanyData(null);
        setShowRenewalModal(false);
      }
    } else {
      setRenewalCompanyData(null);
      setShowRenewalModal(false);
    }
  }, [currentUser, sessionActiveCompany]);

  const handleManualCloudSync = async () => {
    if (isCloudSyncing) return;
    setIsCloudSyncing(true);
    setCloudSyncStatus('syncing');
    try {
      const res = await forceSyncFromFirestore();
      if (res.success) {
        setCloudSyncStatus('success');
        setTimeout(() => setCloudSyncStatus('idle'), 4000);
      } else {
        setCloudSyncStatus('error');
      }
    } catch (e) {
      console.error(e);
      setCloudSyncStatus('error');
    } finally {
      setIsCloudSyncing(false);
    }
  };

  // 1. Subscribe to Firebase Authentication state change listener
  useEffect(() => {
    const unsubAuthListener = registerAuthListener((fbUser, _token, status) => {
      setAuthLifecycleState(status);
      if (status === 'AUTHENTICATED' && fbUser) {
        // Find matching initial user
        const matchedInitial = INITIAL_USERS.find(
          u => (fbUser.email && u.username.toLowerCase() === fbUser.email.toLowerCase()) || 
               (fbUser.email && u.correo?.toLowerCase() === fbUser.email.toLowerCase())
        );

        getOrCreateAuthoritativeUserAuth(fbUser.uid, fbUser.email, matchedInitial).then((authRecord) => {
          setSsotReady(true);
          setCurrentUser((prev) => {
            if (prev) {
              return {
                ...prev,
                companyId: authRecord.companyId || prev.companyId || 'company-insumedic',
                estado: authRecord.estado,
                firebaseUid: fbUser.uid,
                authProvider: 'firebase' as const
              };
            }

            // If prev was null (no active local session), only auto-login if we have a verified email match
            if (matchedInitial || (fbUser.email && !fbUser.isAnonymous)) {
              const matchedById = INITIAL_USERS.find(u => u.id === authRecord.legacyUserId);
              const baseUser = matchedInitial || matchedById || {
                id: authRecord.legacyUserId || `user-${fbUser.uid}`,
                nombreCompleto: fbUser.displayName || fbUser.email?.split('@')[0].toUpperCase() || 'USUARIO AUTENTICADO',
                cedula: fbUser.uid.substring(0, 10),
                cargo: authRecord.role === 'director_tecnico' ? 'Director Técnico y de Calidad' : 'Operario de Reempaque',
                username: fbUser.email || 'usuario.firebase',
                correo: fbUser.email || undefined,
                estado: authRecord.estado,
                esAdmin: authRecord.role === 'director_tecnico' || authRecord.role === 'superadmin',
                modulosPermitidos: ['registro_ordenes', 'disposicion_productos', 'dashboard_productividad', 'gestion_usuarios'],
                companyId: authRecord.companyId,
                firebaseUid: fbUser.uid,
                authProvider: 'firebase' as const
              };

              return {
                ...baseUser,
                companyId: authRecord.companyId,
                estado: authRecord.estado,
                firebaseUid: fbUser.uid,
                authProvider: 'firebase' as const
              };
            }

            // Keep null when anonymous until user logs in via AuthScreen
            return null;
          });
        }).catch((err) => {
          console.warn('Error loading authoritative auth document:', err);
        });
      } else if (status === 'UNAUTHENTICATED') {
        setSsotReady(false);
        cleanupAllRealtimeSync();
      }
    });

    return () => {
      unsubAuthListener();
    };
  }, []);

  // 2. Start the Firestore listeners ONLY when authenticated in Firebase Auth and SSOT authority is ready
  useEffect(() => {
    if (authLifecycleState !== 'AUTHENTICATED' || !currentUser?.firebaseUid || !ssotReady) {
      cleanupAllRealtimeSync();
      return;
    }

    let active = true;

    // Run proactive cloud sync
    const runInitialSync = async () => {
      setIsCloudSyncing(true);
      setCloudSyncStatus('syncing');
      try {
        const res = await forceSyncFromFirestore(currentUser.companyId);
        if (active) {
          if (res.success) {
            setCloudSyncStatus('success');
            setTimeout(() => {
              if (active) setCloudSyncStatus('idle');
            }, 5000);
          } else {
            setCloudSyncStatus('error');
          }
        }
      } catch (e) {
        console.error(e);
        if (active) setCloudSyncStatus('error');
      } finally {
        if (active) setIsCloudSyncing(false);
      }
    };

    runInitialSync();

    const tenantId = currentUser.companyId;

    // 1. Sync companies metadata
    const unsubCompaniesSync = setupRealtimeSync<Company>('companies', 'insumedic_companies', getSavedCompanies(), tenantId);
    const unsubCompaniesRegistry = syncRegistry.subscribe<Company>('companies', () => {
      handleReloadSaaSSession();
    });

    // 2. Sync users list
    const unsubUsersSync = setupRealtimeSync<User>('users', 'insumedic_users', INITIAL_USERS, tenantId);
    const unsubUsersRegistry = syncRegistry.subscribe<User>('users', (data) => {
      setUsers((prev) => {
        if (JSON.stringify(prev) !== JSON.stringify(data)) {
          return data;
        }
        return prev;
      });
      setIsUsersSyncComplete(true);
    });

    // 3. Sync production orders
    const unsubOrdenesSync = setupRealtimeSync<OrdenProduccion>('ordenes', 'insumedic_ordenes_v3', INITIAL_ORDENES, tenantId);
    const unsubOrdenesRegistry = syncRegistry.subscribe<OrdenProduccion>('ordenes', (data) => {
      // [FASE 1 CONTENCIÓN]: AUTO-HEALING DESACTIVADO.
      // Previamente, este suscriptor mutaba y forzaba escrituras a Firestore (uploadItemToFirestore)
      // para corregir 'OP-14072026-1' y sintetizar 'OP-14072026-2'.
      // En Fase 1 el suscriptor actúa como receptor pasivo de datos sin mutar ni escribir en la nube.
      /*
      // --- CÓDIGO DE AUTO-HEALING HISTÓRICO CONSERVADO PARA AUDITORÍA ---
      let changed = false;
      const isTargetOP = (num: string) => {
        if (!num) return false;
        return num === 'OP-14072026-1' || num === 'OP-14072026-2' || num === 'OP-140722026-1' || num === 'OP-140722026-2' || num.includes('14072');
      };
      const corrected = data.map((o) => {
        let updatedOrder = { ...o };
        let itemModified = false;
        if (isTargetOP(o.numeroOrden)) {
          const targetDate = '2026-07-14T12:00:00.000Z';
          const targetQuantity = 100;
          const targetFinReempaque = '2026-07-14T17:35:00';
          if (o.fechaRegistro !== targetDate || o.tamanoLote !== targetQuantity || o.fechaFinReempaque !== targetFinReempaque) {
            updatedOrder.fechaRegistro = targetDate;
            updatedOrder.tamanoLote = targetQuantity;
            updatedOrder.fechaFinReempaque = targetFinReempaque;
            if (!updatedOrder.fechaInicioReempaque) {
              updatedOrder.fechaInicioReempaque = '2026-07-14T12:30:00';
            }
            itemModified = true;
          }
        }
        if (!updatedOrder.elaboradoPor || updatedOrder.elaboradoPor === 'Administrador' || updatedOrder.elaboradoPor === 'Auxiliar/Operario' || updatedOrder.elaboradoPor === 'ADMINISTRADOR DEL SISTEMA') {
          updatedOrder.elaboradoPor = 'LAURA PATRICIA CARRERO PERDOMO';
          itemModified = true;
        }
        if (itemModified) {
          changed = true;
          uploadItemToFirestore<OrdenProduccion>('ordenes', updatedOrder);
        }
        return updatedOrder;
      });
      */

      setOrdenes((prev) => {
        if (JSON.stringify(prev) !== JSON.stringify(data)) {
          return data;
        }
        return prev;
      });
    });

    // 4. Sync product dispositions
    const unsubDisposicionesSync = setupRealtimeSync<DisposicionProducto>('disposiciones', 'insumedic_disposiciones_v3', INITIAL_DISPOSICIONES, tenantId);
    const unsubDisposicionesRegistry = syncRegistry.subscribe<DisposicionProducto>('disposiciones', (data) => {
      // [FASE 1 CONTENCIÓN]: AUTO-HEALING DESACTIVADO.
      // Previamente, este suscriptor mutaba y forzaba escrituras a Firestore (uploadItemToFirestore)
      // para sobreescribir fechas de disposición y sintetizar registros.
      // En Fase 1 el suscriptor actúa como receptor pasivo de datos sin mutar ni escribir en la nube.
      /*
      // --- CÓDIGO DE AUTO-HEALING HISTÓRICO CONSERVADO PARA AUDITORÍA ---
      let changed = false;
      const isTargetOP = (num: string) => {
        if (!num) return false;
        return num === 'OP-14072026-1' || num === 'OP-14072026-2' || num === 'OP-140722026-1' || num === 'OP-140722026-2' || num.includes('14072');
      };
      const correctedDisp = data.map((d) => {
        let updatedDisp = { ...d };
        if (isTargetOP(d.numeroOrden)) {
          const targetFechaDisposicion = '2026-07-14T17:35:00';
          if (d.fechaDisposicion !== targetFechaDisposicion) {
            updatedDisp.fechaDisposicion = targetFechaDisposicion;
            changed = true;
            uploadItemToFirestore<DisposicionProducto>('disposiciones', updatedDisp);
          }
        }
        return updatedDisp;
      });
      */

      setDisposiciones((prev) => {
        if (JSON.stringify(prev) !== JSON.stringify(data)) {
          return data;
        }
        return prev;
      });
    });

    // 5. Sync database backups
    const unsubBackupsSync = setupRealtimeSync<BackupRecord>('backups', 'insumedic_backups_v3', [], tenantId);
    const unsubBackupsRegistry = syncRegistry.subscribe<BackupRecord>('backups', (data) => {
      setBackups((prev) => {
        if (JSON.stringify(prev) !== JSON.stringify(data)) {
          return data;
        }
        return prev;
      });
    });

    // 6. Sync audit activity logs (vitácora) en línea (online)
    const unsubLogsSync = setupRealtimeSync<ActivityLog>('logs', 'insumedic_activity_logs', getSavedLogs(), tenantId);

    return () => {
      active = false;
      unsubCompaniesRegistry();
      unsubUsersRegistry();
      unsubOrdenesRegistry();
      unsubDisposicionesRegistry();
      unsubBackupsRegistry();
      cleanupAllRealtimeSync();
    };
  }, [authLifecycleState, currentUser?.firebaseUid, ssotReady]);

  // Persistence hooks
  useEffect(() => {
    localStorage.setItem('insumedic_users', JSON.stringify(users));
  }, [users]);

  useEffect(() => {
    localStorage.setItem('insumedic_ordenes_v3', JSON.stringify(ordenes));
  }, [ordenes]);

  useEffect(() => {
    localStorage.setItem('insumedic_disposiciones_v3', JSON.stringify(disposiciones));
  }, [disposiciones]);

  useEffect(() => {
    localStorage.setItem('insumedic_backups_v3', JSON.stringify(backups));
  }, [backups]);

  useEffect(() => {
    if (currentUser) {
      sessionStorage.setItem('insumedic_current_user', JSON.stringify(currentUser));
      localStorage.setItem('insumedic_current_user', JSON.stringify(currentUser));
    } else {
      sessionStorage.removeItem('insumedic_current_user');
      localStorage.removeItem('insumedic_current_user');
    }
  }, [currentUser]);

  // Self-cleaning and automatic creation/update of requested Comercializadora Insumedic profiles
  useEffect(() => {
    if (!isUsersSyncComplete) return; // Wait until users are loaded from Firestore!
    if (users && users.length > 0) {
      // Heal check: Ensure none of the initial core profiles have been deleted or missing
      const isAnyPredefinedUserMissing = INITIAL_USERS.some(
        nu => !users.some(u => u.username.toUpperCase() === nu.username.toUpperCase())
      );
      
      const alreadyDone = sessionStorage.getItem('insumedic_forced_insumedic_users_sync_v8_stable');
      if (alreadyDone && !isAnyPredefinedUserMissing) return;

      const ALLOWED_USERNAMES = ['JUAN.VALENCIA', 'LAURA.CARRERO', 'MARIA.CECHAGUA', 'NATIVIDAD.CUELLAR', 'ADM.SISTEMAS', 'ERNESTO.TRILLERAS'];
      let listModified = false;
      let currentList = [...users];

      // 1. Ensure all users in state have their companyId properly mapped
      const mappedList = currentList.map(u => {
        if (!u.companyId) {
          listModified = true;
          const updated = { ...u, companyId: 'company-insumedic' };
          uploadItemToFirestore('users', updated).catch(e => console.warn('Sync update failed:', e));
          return updated;
        }
        return u;
      });

      currentList = mappedList;

      // 2. Add or update predefined users with their correct details/signatures
      INITIAL_USERS.forEach(nu => {
        const idx = currentList.findIndex(u => u.username.toUpperCase() === nu.username.toUpperCase());
        if (idx === -1) {
          listModified = true;
          let signatureToInject = undefined;
          if (nu.username === 'MARIA.CECHAGUA') {
            signatureToInject = generatePredefinedSignature('maria');
          } else if (nu.username === 'NATIVIDAD.CUELLAR') {
            signatureToInject = generatePredefinedSignature('natividad');
          } else {
            signatureToInject = generatePredefinedSignature(nu.username, nu.nombreCompleto);
          }
          const userWithSig = {
            ...nu,
            companyId: 'company-insumedic',
            firma: signatureToInject
          };
          currentList.push(userWithSig);
          uploadItemToFirestore('users', userWithSig).catch(e => console.warn('Sync upload failed:', e));
        } else {
          const existing = currentList[idx];

          // Setup modules to use and check if we need to force correct modules
          let modulosToUse = existing.modulosPermitidos || nu.modulosPermitidos;
          if (nu.username === 'LAURA.CARRERO' && (!modulosToUse.includes('disposicion_productos') || modulosToUse[0] !== 'disposicion_productos')) {
            modulosToUse = ['disposicion_productos', 'registro_ordenes'];
          }

          let needsUpdate = existing.nombreCompleto !== nu.nombreCompleto || 
                            existing.companyId !== 'company-insumedic' ||
                            (nu.username === 'LAURA.CARRERO' && (!existing.modulosPermitidos?.includes('disposicion_productos') || existing.modulosPermitidos[0] !== 'disposicion_productos'));
          
          let signatureToInject = undefined;
          if (nu.username === 'MARIA.CECHAGUA' && !existing.firma) {
            signatureToInject = generatePredefinedSignature('maria');
            needsUpdate = true;
          } else if (nu.username === 'NATIVIDAD.CUELLAR' && !existing.firma) {
            signatureToInject = generatePredefinedSignature('natividad');
            needsUpdate = true;
          } else if (!existing.firma) {
            signatureToInject = generatePredefinedSignature(nu.username, nu.nombreCompleto);
            needsUpdate = true;
          }

          if (needsUpdate) {
            listModified = true;
            const updatedUser = {
              ...nu,
              ...existing,
              nombreCompleto: nu.nombreCompleto,
              companyId: 'company-insumedic',
              modulosPermitidos: modulosToUse,
              // Strictly preserve their existing signature if one is already present
              firma: existing.firma || signatureToInject || generatePredefinedSignature(nu.username, nu.nombreCompleto)
            };
            currentList[idx] = updatedUser;
            uploadItemToFirestore('users', updatedUser).catch(e => console.warn('Sync update failed:', e));
          }
        }
      });

      if (listModified) {
        setUsers(currentList);
        localStorage.setItem('insumedic_users', JSON.stringify(currentList));
      }

      sessionStorage.setItem('insumedic_forced_insumedic_users_sync_v8_stable', 'true');
    }
  }, [users, isUsersSyncComplete]);

  // Handler functions
  const handleUserCreated = (newUser: User) => {
    const isSpecialUser = !currentUser || currentUser?.username.toUpperCase() === 'JUAN.VALENCIA' || currentUser?.cargo === 'Director Técnico y de Calidad';
    if (!isSpecialUser) {
      console.warn('Operación denegada. No tienes permisos para crear colaboradores.');
      return;
    }
    const assignWithTenant = {
      ...newUser,
      companyId: newUser.companyId || currentUser?.companyId || 'company-insumedic'
    };
    setUsers((prev) => [...prev, assignWithTenant]);
    uploadItemToFirestore<User>('users', assignWithTenant);
    
    addAuditLog(
      currentUser?.nombreCompleto || 'Sistema',
      'Usuarios',
      'Crear Colaborador',
      `Creado nuevo colaborador: ${newUser.nombreCompleto} (${newUser.cargo})`,
      currentUser?.companyId || newUser.companyId || 'company-insumedic',
      sessionActiveCompany?.nombreEmpresa || 'Portal Corporativo'
    );
  };

  const handleUpdateUserStatus = (userId: string, newStatus: 'ACTIVO' | 'INACTIVO') => {
    const isSpecialUser = currentUser?.username.toUpperCase() === 'JUAN.VALENCIA' || currentUser?.cargo === 'Director Técnico y de Calidad';
    if (!isSpecialUser) {
      console.warn('Operación denegada. No tienes permisos para cambiar el estado de colaboradores.');
      return;
    }
    setUsers((prev) => {
      const updated = prev.map((user) => (user.id === userId ? { ...user, estado: newStatus } : user));
      const targetUser = updated.find(u => u.id === userId);
      if (targetUser) {
        uploadItemToFirestore<User>('users', targetUser);
      }
      return updated;
    });

    const userObj = users.find(u => u.id === userId);
    addAuditLog(
      currentUser?.nombreCompleto || 'Sistema',
      'Usuarios',
      'Estatus Modificado',
      `Modificado estatus de usuario: ${userObj?.nombreCompleto} a ${newStatus}`,
      currentUser?.companyId,
      sessionActiveCompany?.nombreEmpresa
    );
  };

  const handleUpdateUserPassword = (userId: string, newPassword: string) => {
    const isSpecialUser = currentUser?.username.toUpperCase() === 'JUAN.VALENCIA' || currentUser?.cargo === 'Director Técnico y de Calidad';
    if (!isSpecialUser) {
      console.warn('Operación denegada. No tienes permisos para actualizar claves.');
      return;
    }
    setUsers((prev) => {
      const updated = prev.map((user) => (user.id === userId ? { ...user, password: newPassword } : user));
      const targetUser = updated.find(u => u.id === userId);
      if (targetUser) {
        uploadItemToFirestore<User>('users', targetUser);
      }
      return updated;
    });
    if (currentUser && currentUser.id === userId) {
      setCurrentUser((prev) => prev ? { ...prev, password: newPassword } : null);
    }

    const userObj = users.find(u => u.id === userId);
    addAuditLog(
      currentUser?.nombreCompleto || 'Sistema',
      'Usuarios',
      'Clave Modificada',
      `Clave personal re-encriptada de forma exitosa para: ${userObj?.nombreCompleto}`,
      currentUser?.companyId,
      sessionActiveCompany?.nombreEmpresa
    );
  };

  const handleUpdateUserModules = (userId: string, newModules: string[]) => {
    const isSpecialUser = currentUser?.username.toUpperCase() === 'JUAN.VALENCIA' || currentUser?.cargo === 'Director Técnico y de Calidad';
    if (!isSpecialUser) {
      console.warn('Operación denegada. No tienes permisos para actualizar permisos de módulos.');
      return;
    }
    setUsers((prev) => {
      const updated = prev.map((user) => (user.id === userId ? { ...user, modulosPermitidos: newModules } : user));
      const targetUser = updated.find(u => u.id === userId);
      if (targetUser) {
        uploadItemToFirestore<User>('users', targetUser);
      }
      return updated;
    });
    if (currentUser && currentUser.id === userId) {
      setCurrentUser((prev) => prev ? { ...prev, modulosPermitidos: newModules } : null);
    }

    const userObj = users.find(u => u.id === userId);
    addAuditLog(
      currentUser?.nombreCompleto || 'Sistema',
      'Usuarios',
      'Permisos Modificados',
      `Módulos permitidos actualizados para el colaborador: ${userObj?.nombreCompleto}`,
      currentUser?.companyId,
      sessionActiveCompany?.nombreEmpresa
    );
  };

  const handleUpdateUserSignature = (userId: string, signatureBase64: string) => {
    setUsers((prev) => {
      const updated = prev.map((user) => (user.id === userId ? { ...user, firma: signatureBase64 } : user));
      const targetUser = updated.find(u => u.id === userId);
      if (targetUser) {
        uploadItemToFirestore<User>('users', targetUser);
      }
      return updated;
    });

    if (currentUser && currentUser.id === userId) {
      setCurrentUser((prev) => prev ? { ...prev, firma: signatureBase64 } : null);
    }

    const userObj = users.find(u => u.id === userId);
    addAuditLog(
      currentUser?.nombreCompleto || 'Sistema',
      'Usuarios',
      'Firma Digital Actualizada',
      `Firma digital cargada/actualizada con éxito para: ${userObj?.nombreCompleto}`,
      currentUser?.companyId,
      sessionActiveCompany?.nombreEmpresa
    );
  };

  const handleDeleteUser = (userId: string) => {
    const isSpecialUser = currentUser?.username.toUpperCase() === 'JUAN.VALENCIA' || currentUser?.cargo === 'Director Técnico y de Calidad';
    if (!isSpecialUser) {
      console.warn('Operación denegada. No tienes permisos para eliminar colaboradores.');
      return;
    }
    const userObj = users.find(u => u.id === userId);
    setUsers((prev) => prev.filter((user) => user.id !== userId));
    deleteItemFromFirestore('users', userId);

    addAuditLog(
      currentUser?.nombreCompleto || 'Sistema',
      'Usuarios',
      'Colaborador Eliminado',
      `Usuario colaborador removido permanentemente: ${userObj?.nombreCompleto}`,
      currentUser?.companyId,
      sessionActiveCompany?.nombreEmpresa
    );
  };

  const handleCreateBackup = async (tipo: 'AUTOMATICA (18:00)' | 'MANUAL') => {
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    const timeStr = now.toTimeString().split(' ')[0]; // HH:MM:SS
    const backupId = `backup-${tipo === 'MANUAL' ? 'manual' : 'auto'}-${dateStr}-${now.getTime()}`;
    
    const backupPayload = {
      ordenes: [...tenantFilteredOrdenes],
      disposiciones: [...tenantFilteredDisposiciones],
      users: [...tenantFilteredUsers]
    };

    const sha256Hash = await computeSha256(backupPayload);

    const newBackup: BackupRecord = {
      id: backupId,
      fechaHora: `${dateStr} ${timeStr}`,
      timestamp: now.toISOString(),
      ordenesCount: tenantFilteredOrdenes.length,
      disposicionesCount: tenantFilteredDisposiciones.length,
      usersCount: tenantFilteredUsers.length,
      tipo,
      companyId: currentUser?.companyId || 'company-insumedic',
      hashSha256: sha256Hash,
      hashAlgorithm: 'SHA-256',
      schemaVersion: '1.0.1',
      createdBy: currentUser?.username || 'SYSTEM',
      status: 'COMPLETED',
      data: backupPayload
    };
    
    setBackups((prev) => [newBackup, ...prev]);
    uploadItemToFirestore<BackupRecord>('backups', newBackup);
    
    addAuditLog(
      currentUser?.nombreCompleto || 'Sistema Automático',
      'Copias Seguridad',
      'Crear Copia',
      `Copia de seguridad ${tipo} generada con éxito (SHA-256: ${sha256Hash.substring(0, 16)}..., ${tenantFilteredOrdenes.length} órdenes, ${tenantFilteredDisposiciones.length} disposiciones).`,
      currentUser?.companyId,
      sessionActiveCompany?.nombreEmpresa
    );
    
    return newBackup;
  };

  const handleRestoreCompleted = async () => {
    // Refresh state directly from Firestore after authoritative server-side restoration
    await handleManualCloudSync();
  };

  const handleDeleteBackup = (backupId: string) => {
    const isSuperAdmin = currentUser?.username.toUpperCase() === 'JUAN.VALENCIA';
    if (!isSuperAdmin) {
      alert('Operación denegada por política de seguridad: La eliminación de copias históricas está restringida exclusivamente al SuperAdministrador del Sistema.');
      return;
    }

    setBackups((prev) => prev.filter(b => b.id !== backupId));
    deleteItemFromFirestore('backups', backupId);
    
    addAuditLog(
      currentUser?.nombreCompleto || 'Sistema',
      'Copias Seguridad',
      'Borrar Copia',
      `Copia de seguridad removida por SuperAdministrador: ID ${backupId}`,
      currentUser?.companyId,
      sessionActiveCompany?.nombreEmpresa
    );
  };

  const handleLoginSuccess = (user: User) => {
    setCurrentUser(user);
    if (user.username.toUpperCase() === 'LAURA.CARRERO') {
      setActiveTab('disposicion_productos');
    } else if (user.username.toLowerCase() === 'administrador' || user.username.toLowerCase() === 'juan.valencia') {
      setActiveTab('registro_ordenes');
    } else if (user.username === 'admin') {
      setActiveTab('registro_ordenes');
    } else if (user.esAdmin || !user.modulosPermitidos || user.modulosPermitidos.length === 0) {
      setActiveTab('registro_ordenes');
    } else {
      setActiveTab(user.modulosPermitidos[0] as MenuTab);
    }
  };

  const handleLogout = async () => {
    if (currentUser) {
      addAuditLog(
        currentUser.nombreCompleto,
        'Seguridad',
        'Cierre de Sesión',
        'Sesión terminada por el usuario',
        currentUser.companyId,
        sessionActiveCompany?.nombreEmpresa
      );
    }
    await logoutFirebaseAuth().catch(e => console.warn('Firebase Auth logout non-blocking warning:', e));
    setSsotReady(false);
    setCurrentUser(null);
    setSessionActiveCompany(null);
  };

  const handleAddOrden = (nuevaOrden: OrdenProduccion) => {
    const orderWithCompany = {
      ...nuevaOrden,
      companyId: currentUser?.companyId || 'company-insumedic'
    };
    setOrdenes((prev) => [orderWithCompany, ...prev]);
    uploadItemToFirestore<OrdenProduccion>('ordenes', orderWithCompany);

    addAuditLog(
      currentUser?.nombreCompleto || 'Operador',
      'Órdenes',
      'Guardar Orden',
      `Registró orden de fabricación número ${nuevaOrden.numeroOrden} de ${nuevaOrden.nombreGenerico}`,
      currentUser?.companyId,
      sessionActiveCompany?.nombreEmpresa
    );
  };

  const handleDeleteOrden = (id: string) => {
    const ordObj = ordenes.find(o => o.id === id);
    disposiciones.forEach(d => {
      if (d.ordenId === id) {
        deleteItemFromFirestore('disposiciones', d.id);
      }
    });

    setDisposiciones((prev) => prev.filter((d) => d.ordenId !== id));
    setOrdenes((prev) => prev.filter((o) => o.id !== id));
    deleteItemFromFirestore('ordenes', id);

    addAuditLog(
      currentUser?.nombreCompleto || 'Operador',
      'Órdenes',
      'Eliminar Registro',
      `Removió orden N° ${ordObj?.numeroOrden} y sus disposiciones de forma administrativa`,
      currentUser?.companyId,
      sessionActiveCompany?.nombreEmpresa
    );
  };

  const handleDeleteMultipleOrdenes = (ids: string[]) => {
    disposiciones.forEach(d => {
      if (ids.includes(d.ordenId)) {
        deleteItemFromFirestore('disposiciones', d.id);
      }
    });
    ids.forEach(id => {
      deleteItemFromFirestore('ordenes', id);
    });

    setDisposiciones((prev) => prev.filter((d) => !ids.includes(d.ordenId)));
    setOrdenes((prev) => {
      const updated = prev.filter((o) => !ids.includes(o.id));
      if (updated.length === 0) {
        localStorage.setItem('insumedic_op_consecutive', '1');
      }
      return updated;
    });

    addAuditLog(
      currentUser?.nombreCompleto || 'Operador',
      'Órdenes',
      'Borrado Masivo',
      `Remoción masiva de ${ids.length} lotes por depuración`,
      currentUser?.companyId,
      sessionActiveCompany?.nombreEmpresa
    );
  };

  const handleAddDisposicion = (nuevaDisp: DisposicionProducto) => {
    const dispWithCompany = {
      ...nuevaDisp,
      companyId: currentUser?.companyId || 'company-insumedic'
    };
    setDisposiciones((prev) => [dispWithCompany, ...prev]);
    uploadItemToFirestore<DisposicionProducto>('disposiciones', dispWithCompany);

    addAuditLog(
      currentUser?.nombreCompleto || 'Responsable',
      'Disposición',
      'Registrar Disposición',
      `Dispuso de ${nuevaDisp.cantidadDispuesta} uds de la orden ${nuevaDisp.numeroOrden} en estado: ${nuevaDisp.estadoDisposicion}`,
      currentUser?.companyId,
      sessionActiveCompany?.nombreEmpresa
    );
  };

  const handleUpdateDisposicion = (updatedDisp: DisposicionProducto) => {
    const dispWithCompany = {
      ...updatedDisp,
      companyId: currentUser?.companyId || 'company-insumedic'
    };
    setDisposiciones((prev) => prev.map(d => d.id === updatedDisp.id ? dispWithCompany : d));
    uploadItemToFirestore<DisposicionProducto>('disposiciones', dispWithCompany);

    addAuditLog(
      currentUser?.nombreCompleto || 'Responsable',
      'Disposición',
      'Actualizar Disposición',
      `Modificó y editó la disposición N° ${updatedDisp.id} (Orden: ${updatedDisp.numeroOrden})`,
      currentUser?.companyId,
      sessionActiveCompany?.nombreEmpresa
    );
  };

  const handleClearAllData = () => {
    // Reset all automatic counters to 1
    localStorage.setItem('insumedic_op_consecutive', '1');

    if (currentUser?.username === 'admin' || currentUser?.username.toLowerCase() === 'administrador' || currentUser?.username.toLowerCase() === 'juan.valencia') {
      ordenes.forEach(o => deleteItemFromFirestore('ordenes', o.id));
      disposiciones.forEach(d => deleteItemFromFirestore('disposiciones', d.id));
      setOrdenes([]);
      setDisposiciones([]);
    } else {
      const targetCompanyId = currentUser?.companyId || 'company-insumedic';
      ordenes.forEach(o => {
        const orderCompanyId = o.companyId || 'company-insumedic';
        if (orderCompanyId === targetCompanyId) {
          deleteItemFromFirestore('ordenes', o.id);
        }
      });
      disposiciones.forEach(d => {
        const dispCompanyId = d.companyId || 'company-insumedic';
        if (dispCompanyId === targetCompanyId) {
          deleteItemFromFirestore('disposiciones', d.id);
        }
      });
      setOrdenes(prev => prev.filter(o => {
        const orderCompanyId = o.companyId || 'company-insumedic';
        return orderCompanyId !== targetCompanyId;
      }));
      setDisposiciones(prev => prev.filter(d => {
        const dispCompanyId = d.companyId || 'company-insumedic';
        return dispCompanyId !== targetCompanyId;
      }));
    }
    
    addAuditLog(
      currentUser?.nombreCompleto || 'Gestor',
      'Avanzado',
      'Depuración Base de Datos',
      `Limpió todos los datos de fabricación de la planta`,
      currentUser?.companyId,
      sessionActiveCompany?.nombreEmpresa
    );
  };

  const handleUpdateOrdenStatus = (
    id: string, 
    newStatus: 'Pendiente por Aceptar' | 'Aceptada' | 'Cerrada' | 'Cancelada',
    extraFields?: Partial<OrdenProduccion>
  ) => {
    const originalOrden = ordenes.find(o => o.id === id);
    setOrdenes((prev) => {
      const updated = prev.map((o) => (o.id === id ? { ...o, estadoOrden: newStatus, ...extraFields } : o));
      const targetOrden = updated.find(o => o.id === id);
      if (targetOrden) {
        uploadItemToFirestore<OrdenProduccion>('ordenes', targetOrden);
      }
      return updated;
    });

    if (originalOrden) {
      let detalleCambio = `Cambió estado de la orden ${originalOrden.numeroOrden} de "${originalOrden.estadoOrden}" a "${newStatus}".`;
      if (extraFields && Object.keys(extraFields).length > 0) {
        const modificados = Object.keys(extraFields).map(k => {
          if (k === 'fechaInicioReempaque') return 'Fecha Inicio Reempaque';
          if (k === 'fechaFinReempaque') return 'Fecha Fin Reempaque';
          if (k === 'elaboradoPor') return 'Elaborado Por';
          if (k === 'verificadoPor') return 'Verificado Por';
          return k;
        }).join(', ');
        detalleCambio += ` Modificaciones en: ${modificados}.`;
      }
      addAuditLog(
        currentUser?.nombreCompleto || 'Sistema/Calidad',
        'Órdenes',
        'Modificar Orden / Flujo',
        detalleCambio,
        currentUser?.companyId,
        sessionActiveCompany?.nombreEmpresa
      );
    }
  };

  // SaaS Block validations
  const isReadOnlyMode = (): boolean => {
    if (!sessionActiveCompany) return false;
    if (currentUser?.username === 'admin' || currentUser?.username.toLowerCase() === 'administrador' || currentUser?.username.toLowerCase() === 'juan.valencia') return false;
    return isCompanySubscriptionExpired(sessionActiveCompany);
  };

  const isSaaSAccessExpired = (): boolean => {
    // Subscription expiration now grants Read-Only access (Modo Consulta) instead of a hard overlay block
    return false;
  };

  const isSaaSBlocked = (): boolean => {
    if (!sessionActiveCompany) return false;
    if (currentUser?.username === 'admin' || currentUser?.username.toLowerCase() === 'administrador' || currentUser?.username.toLowerCase() === 'juan.valencia') return false;
    return sessionActiveCompany.estadoAprobacion === 'BLOQUEADO' || sessionActiveCompany.estadoAprobacion === 'PENDIENTE';
  };

  // Multi-tenant Scoping Filters (memoized for reference stability)
  const tenantFilteredOrdenes = useMemo(() => {
    return ordenes.filter(o => {
      if (currentUser?.username === 'admin' || currentUser?.username.toLowerCase() === 'administrador' || currentUser?.username.toLowerCase() === 'juan.valencia') return true;
      const uCId = currentUser?.companyId || 'company-insumedic';
      const orderCId = o.companyId || 'company-insumedic';
      if (uCId.startsWith('company-insumedic') && orderCId.startsWith('company-insumedic')) {
        return true;
      }
      return orderCId === uCId;
    });
  }, [ordenes, currentUser]);

  const tenantFilteredDisposiciones = useMemo(() => {
    return disposiciones.filter(d => {
      if (currentUser?.username === 'admin' || currentUser?.username.toLowerCase() === 'administrador' || currentUser?.username.toLowerCase() === 'juan.valencia') return true;
      const uCId = currentUser?.companyId || 'company-insumedic';
      const dispCId = d.companyId || 'company-insumedic';
      if (uCId.startsWith('company-insumedic') && dispCId.startsWith('company-insumedic')) {
        return true;
      }
      return dispCId === uCId;
    });
  }, [disposiciones, currentUser]);

  const tenantFilteredUsers = useMemo(() => {
    return users.filter(u => {
      if (currentUser?.username === 'admin' || currentUser?.username.toLowerCase() === 'administrador' || currentUser?.username.toLowerCase() === 'juan.valencia') return true;
      const uCId = currentUser?.companyId || 'company-insumedic';
      const userCId = u.companyId || 'company-insumedic';
      if (uCId.startsWith('company-insumedic') && userCId.startsWith('company-insumedic')) {
        return true;
      }
      return userCId === uCId;
    });
  }, [users, currentUser]);

  // Automated Daily Backup at 18:00 (6:00 PM) LatAm / Client local time
  useEffect(() => {
    const intervalId = setInterval(() => {
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      
      // Check if it's 18:00 hours (6 PM)
      if (hours === 18 && minutes === 0) {
        const todayDate = now.toISOString().split('T')[0];
        const lastAutoBackupKey = `insumedic_last_auto_backup_${todayDate}`;
        const alreadyDoneToday = localStorage.getItem(lastAutoBackupKey);
        
        if (!alreadyDoneToday) {
          localStorage.setItem(lastAutoBackupKey, 'true');
          console.log('Ejecutando Copia de Seguridad Automática programada a las 18:00 Horas...');
          handleCreateBackup('AUTOMATICA (18:00)');
        }
      }
    }, 15000); // Check every 15 seconds
    
    return () => clearInterval(intervalId);
  }, [tenantFilteredOrdenes, tenantFilteredDisposiciones, tenantFilteredUsers, currentUser, sessionActiveCompany]);

  return (
    <div className="min-h-screen bg-slate-150 flex flex-col justify-between selection:bg-teal-500/10 selection:text-teal-900">
      
      {/* 1. Loading State while Firebase Auth resolves */}
      {authLifecycleState === 'AUTH_LOADING' ? (
        <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white" id="auth-loading-screen">
          <div className="flex flex-col items-center space-y-4 max-w-md text-center p-8">
            <div className="w-12 h-12 border-4 border-teal-500/20 border-t-teal-400 rounded-full animate-spin"></div>
            <div className="space-y-1">
              <h2 className="text-base font-bold text-white tracking-wide uppercase font-mono">PHARMA TRACE</h2>
              <p className="text-xs text-slate-400">Verificando sesión segura e identidad institucional...</p>
            </div>
          </div>
        </div>
      ) : !currentUser ? (
        /* 2. Unauthenticated / Login Screen */
        <AuthScreen
          users={users}
          onUserCreated={handleUserCreated}
          onLoginSuccess={(user) => {
            setInactivityNotice(null);
            handleLoginSuccess(user);
          }}
          inactivityNotice={inactivityNotice}
          onClearInactivityNotice={() => setInactivityNotice(null)}
        />
      ) : (
        /* 3. Authenticated Workspace */
        <div className="flex-1 flex flex-col" id="authenticated-app-frame">
          {/* Top branding bar */}
          <Header 
            currentUser={currentUser} 
            onLogout={handleLogout} 
            isSyncing={isCloudSyncing}
            syncStatus={cloudSyncStatus}
            onForceSync={handleManualCloudSync}
            renewalAlertDays={renewalCompanyData ? renewalCompanyData.diasRestantes : null}
            onOpenRenewalModal={() => setShowRenewalModal(true)}
          />

          {/* Core Applet Workspace with dynamic horizontal menu */}
          <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full space-y-6">
            
            {/* LOCKET SUSPENSION / EXPIRED ACCESS CONTROL OVERLAY BLOCK SCREEN */}
            {isSaaSAccessExpired() ? (
              <motion.div 
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white rounded-3xl border border-slate-200/90 shadow-2xl p-6 sm:p-10 select-text max-w-2xl mx-auto text-center space-y-6"
                id="blockscreen-trial-expired"
              >
                <div className="w-16 h-16 bg-rose-50 border border-rose-100 rounded-2xl flex items-center justify-center mx-auto text-rose-600">
                  <Lock className="h-8 w-8 text-rose-650" />
                </div>
                
                <div className="space-y-2">
                  <h2 className="text-xl sm:text-2xl font-sans font-black text-slate-900 leading-tight uppercase">
                    🔒 ACCESO DE PLAN CANCELADO O SUSPENDIDO
                  </h2>
                  <p className="text-xs text-slate-500 font-sans max-w-lg mx-auto leading-relaxed">
                    Estimado usuario de <strong className="font-bold text-slate-850">{sessionActiveCompany?.nombreEmpresa}</strong>. La prueba de 15 días o el periodo contratado de suscripción para su empresa ha culminado. Sus datos continúan custodiados y seguros bajo regulaciones INVIMA.
                  </p>
                </div>

                <div className="p-5 bg-slate-50 border border-slate-200 rounded-2xl max-w-md mx-auto space-y-4">
                  <div className="text-xs text-left space-y-2">
                    <span className="block font-sans font-black text-slate-800 text-[11px] uppercase tracking-wider text-center">Detalle del Plan de Renovación</span>
                    <div className="flex justify-between items-center text-slate-600 pt-1.5 border-t border-slate-200">
                      <span>Plan SaaS Recomendado:</span>
                      <span className="font-bold text-slate-800 font-sans">SaaS Profesional Mensual</span>
                    </div>
                    <div className="flex justify-between items-center text-slate-650">
                      <span>Módulos de Trazabilidad:</span>
                      <span className="font-mono text-emerald-800 font-bold bg-slate-200/50 px-2 py-0.5 rounded-md">Completo</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-dashed border-slate-200">
                      <span className="font-bold">Total Liquidación:</span>
                      <span className="font-sans font-black text-emerald-800 text-sm">$149.000 COP / mes</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 pt-4">
                  <SuscripcionPortal 
                    currentUser={currentUser} 
                    onRefreshSession={handleReloadSaaSSession} 
                  />
                </div>
              </motion.div>
            ) : isSaaSBlocked() ? (
              <div className="bg-white rounded-3xl border border-slate-250 p-10 max-w-xl mx-auto text-center space-y-5 shadow-2xl">
                <div className="w-16 h-16 bg-red-50 text-red-600 rounded-2xl flex items-center justify-center mx-auto">
                  <AlertCircle className="h-8 w-8" />
                </div>
                <h2 className="text-lg font-sans font-black text-slate-950 uppercase leading-none">🚫 ACCESO DE CLIENTE BLOQUEADO</h2>
                <p className="text-xs text-slate-500 leading-relaxed font-sans max-w-sm mx-auto">
                  Su cuenta corporativa para <strong className="font-bold">{sessionActiveCompany?.nombreEmpresa}</strong> se encuentra en estatus <strong>{sessionActiveCompany?.estadoAprobacion}</strong>. Por favor, comuníquese con el Administrador de Quality.
                </p>
                <button
                  onClick={handleLogout}
                  className="px-5 py-2.5 bg-slate-900 text-white rounded-xl text-xs font-sans font-bold cursor-pointer transition-colors"
                >
                  Regresar al Inicio
                </button>
              </div>
            ) : (
              /* REST OF CORE APPLICATIONS WORKSPACE */
              <>
                {/* Read-Only Mode Banner (Modo Consulta) when subscription is expired */}
                {isReadOnlyMode() && (
                  <div className="bg-amber-500 text-white font-sans p-4 rounded-2xl shadow-lg border border-amber-600 flex flex-col sm:flex-row items-center justify-between gap-3 animate-fade-in">
                    <div className="flex items-center space-x-3">
                      <AlertTriangle className="h-6 w-6 text-amber-100 shrink-0 animate-pulse" />
                      <div className="text-xs sm:text-sm">
                        <span className="font-extrabold uppercase tracking-wide block">Modo Consulta Activo (Licencia Vencida)</span>
                        <span className="text-amber-100 font-medium">Su periodo de suscripción ha vencido. Puede visualizar, consultar y descargar reportes, pero las funciones de ingreso, edición y eliminación de datos están deshabilitadas hasta la renovación.</span>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowRenewalModal(true)}
                      className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-black tracking-wider uppercase transition-colors cursor-pointer shrink-0 border border-amber-300 shadow-sm flex items-center gap-1.5"
                    >
                      <RefreshCw className="h-3.5 w-3.5 text-amber-300" />
                      <span>Renovar Licencia</span>
                    </button>
                  </div>
                )}

                {/* Information Preservation & Automatic Daily Backup Sentinel Banner */}
                <div className="bg-white rounded-2xl border border-sky-100 p-4 shadow-3xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 font-sans ring-1 ring-sky-50/50">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-sky-50 text-sky-600 rounded-xl border border-sky-100/80">
                      <Database className="h-4.5 w-4.5" />
                    </div>
                    <div>
                      <span className="block font-black text-slate-850 text-xs uppercase leading-none tracking-tight">PROTECCIÓN DE INFORMACIÓN ACTIVA</span>
                      <span className="block text-[10px] text-slate-500 mt-1 font-medium">
                        Copia de seguridad del sistema programada diariamente a las <strong className="font-bold text-slate-700">18:00 horas</strong>. Estado: <span className="inline-flex items-center gap-1 text-emerald-750 font-black bg-emerald-50 px-1.5 py-0.5 rounded-sm border border-emerald-150 ml-1 text-[9px] uppercase"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse"></span>✔ Programado</span>
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowBackupModal(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 border border-slate-900 text-white font-extrabold text-[11px] rounded-lg hover:bg-slate-950 transition-all cursor-pointer shadow-3xs hover:shadow-2xs uppercase tracking-wide"
                    >
                      <RefreshCw className="h-3 w-3" />
                      <span>Gestor de Resguardos</span>
                    </button>
                  </div>
                </div>

                {/* Dynamic menu bar selector */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/90 dark:border-slate-800 shadow-xs overflow-hidden">
                  <nav className="flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x divide-slate-150/80 dark:divide-slate-800 bg-slate-100/50 dark:bg-slate-900/80 overflow-x-auto" id="main-navigation-menu">
                    
                    {/* MENU TAB 1: REGISTRO DE ORDENES */}
                    {(currentUser.esAdmin || currentUser.modulosPermitidos?.includes('registro_ordenes')) && (
                      <button
                        onClick={() => setActiveTab('registro_ordenes')}
                        className={`flex-1 min-w-44 px-4 py-3.5 text-left font-sans flex items-center space-x-3 transition-all cursor-pointer focus:outline-hidden ${
                          activeTab === 'registro_ordenes'
                            ? 'bg-white dark:bg-slate-900 text-[#071A36] dark:text-blue-100 shadow-xs border-b-2 border-[#1C4F8A]'
                            : 'text-slate-500 dark:text-slate-400 hover:text-[#071A36] dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-850'
                        }`}
                        id="tab-registro-ordenes"
                      >
                        <div className={`p-2 rounded-xl shrink-0 transition-colors ${
                          activeTab === 'registro_ordenes' ? 'bg-[#1C4F8A] text-white shadow-2xs' : 'bg-slate-200/60 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
                        }`}>
                          <FileText className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <span className={`block text-[11px] font-extrabold uppercase tracking-wide leading-none ${activeTab === 'registro_ordenes' ? 'text-[#071A36] dark:text-blue-100' : ''}`}>
                            ÓRDENES
                          </span>
                          <span className="block text-[9px] text-slate-400 mt-1 font-semibold truncate uppercase tracking-wider">
                            Fraccionamientos
                          </span>
                        </div>
                      </button>
                    )}

                    {/* MENU TAB 2: DISPOSICION PRODUCTOS */}
                    {(currentUser.esAdmin || currentUser.modulosPermitidos?.includes('disposicion_productos')) && (
                      <button
                        onClick={() => setActiveTab('disposicion_productos')}
                        className={`flex-1 min-w-44 px-4 py-3.5 text-left font-sans flex items-center space-x-3 transition-all cursor-pointer focus:outline-hidden ${
                          activeTab === 'disposicion_productos'
                            ? 'bg-white dark:bg-slate-900 text-[#071A36] dark:text-blue-100 shadow-xs border-b-2 border-[#1C4F8A]'
                            : 'text-slate-500 dark:text-slate-400 hover:text-[#071A36] dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-850'
                        }`}
                        id="tab-disposicion-productos"
                      >
                        <div className={`p-2 rounded-xl shrink-0 transition-colors ${
                          activeTab === 'disposicion_productos' ? 'bg-[#1C4F8A] text-white shadow-2xs' : 'bg-slate-200/60 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
                        }`}>
                          <ClipboardCheck className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <span className={`block text-[11px] font-extrabold uppercase tracking-wide leading-none ${activeTab === 'disposicion_productos' ? 'text-[#071A36] dark:text-blue-100' : ''}`}>
                            DISPOSICIÓN
                          </span>
                          <span className="block text-[9px] text-slate-400 mt-1 font-semibold truncate uppercase tracking-wider">
                            Seguimiento Lote
                          </span>
                        </div>
                      </button>
                    )}

                    {/* MENU TAB 3: DASHBOARD */}
                    {(currentUser.esAdmin || currentUser.modulosPermitidos?.includes('dashboard_productividad')) && (
                      <button
                        onClick={() => setActiveTab('dashboard_productividad')}
                        className={`flex-1 min-w-44 px-4 py-3.5 text-left font-sans flex items-center space-x-3 transition-all cursor-pointer focus:outline-hidden ${
                          activeTab === 'dashboard_productividad'
                            ? 'bg-white dark:bg-slate-900 text-[#071A36] dark:text-blue-100 shadow-xs border-b-2 border-[#1C4F8A]'
                            : 'text-slate-500 dark:text-slate-400 hover:text-[#071A36] dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-850'
                        }`}
                        id="tab-dashboard-productividad"
                      >
                        <div className={`p-2 rounded-xl shrink-0 transition-colors ${
                          activeTab === 'dashboard_productividad' ? 'bg-[#1C4F8A] text-white shadow-2xs' : 'bg-slate-200/60 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
                        }`}>
                          <BarChart3 className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <span className={`block text-[11px] font-extrabold uppercase tracking-wide leading-none ${activeTab === 'dashboard_productividad' ? 'text-[#071A36] dark:text-blue-100' : ''}`}>
                            RENDIMIENTO
                          </span>
                          <span className="block text-[9px] text-slate-400 mt-1 font-semibold truncate uppercase tracking-wider">
                            Indicadores
                          </span>
                        </div>
                      </button>
                    )}

                    {/* MENU TAB 4: USUARIOS */}
                    {(currentUser.esAdmin || currentUser.modulosPermitidos?.includes('gestion_usuarios')) && (
                      <button
                        onClick={() => setActiveTab('gestion_usuarios')}
                        className={`flex-1 min-w-44 px-4 py-3.5 text-left font-sans flex items-center space-x-3 transition-all cursor-pointer focus:outline-hidden ${
                          activeTab === 'gestion_usuarios'
                            ? 'bg-white dark:bg-slate-900 text-[#071A36] dark:text-blue-100 shadow-xs border-b-2 border-[#1C4F8A]'
                            : 'text-slate-500 dark:text-slate-400 hover:text-[#071A36] dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-850'
                        }`}
                        id="tab-gestion-usuarios"
                      >
                        <div className={`p-2 rounded-xl shrink-0 transition-colors ${
                          activeTab === 'gestion_usuarios' ? 'bg-[#1C4F8A] text-white shadow-2xs' : 'bg-slate-200/60 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
                        }`}>
                          <Users className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <span className={`block text-[11px] font-extrabold uppercase tracking-wide leading-none ${activeTab === 'gestion_usuarios' ? 'text-[#071A36] dark:text-blue-100' : ''}`}>
                            EQUIPO
                          </span>
                          <span className="block text-[9px] text-slate-400 mt-1 font-semibold truncate uppercase tracking-wider">
                            Asistencial
                          </span>
                        </div>
                      </button>
                    )}

                    {/* MENU TAB 5: GESTOR RESGUARDO */}
                    {(currentUser.cargo === 'Director Técnico y de Calidad' ||
                      currentUser.cargo === 'Administrador del Sistema' ||
                      currentUser.esAdmin === true ||
                      currentUser.username.toLowerCase() === 'administrador' ||
                      currentUser.username.toUpperCase() === 'JUAN.VALENCIA') && (
                      <button
                        onClick={() => setActiveTab('gestor_resguardo')}
                        className={`flex-1 min-w-44 px-4 py-3.5 text-left font-sans flex items-center space-x-3 transition-all cursor-pointer focus:outline-hidden ${
                          activeTab === 'gestor_resguardo'
                            ? 'bg-white dark:bg-slate-900 text-[#071A36] dark:text-blue-100 shadow-xs border-b-2 border-[#1C4F8A]'
                            : 'text-slate-500 dark:text-slate-400 hover:text-[#071A36] dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-850'
                        }`}
                        id="tab-gestor-resguardo"
                      >
                        <div className={`p-2 rounded-xl shrink-0 transition-colors ${
                          activeTab === 'gestor_resguardo' ? 'bg-[#1C4F8A] text-white shadow-2xs' : 'bg-slate-200/60 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
                        }`}>
                          <Database className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <span className={`block text-[11px] font-extrabold uppercase tracking-wide leading-none ${activeTab === 'gestor_resguardo' ? 'text-[#071A36] dark:text-blue-100' : ''}`}>
                            RESGUARDOS
                          </span>
                          <span className="block text-[9px] text-slate-400 mt-1 font-semibold truncate uppercase tracking-wider">
                            Seguridad
                          </span>
                        </div>
                      </button>
                    )}

                    {/* MENU TAB 6: ADMIN GLOBAL SAAS */}
                    {(currentUser.username.toLowerCase() === 'administrador' || currentUser.username.toLowerCase() === 'juan.valencia') && (
                      <button
                        onClick={() => setActiveTab('saas_admin')}
                        className={`flex-1 min-w-44 px-4 py-3.5 text-left font-sans flex items-center space-x-3 transition-all cursor-pointer focus:outline-hidden ${
                          activeTab === 'saas_admin'
                            ? 'bg-white dark:bg-slate-900 text-[#071A36] dark:text-blue-100 shadow-xs border-b-2 border-[#1C4F8A]'
                            : 'text-slate-500 dark:text-slate-400 hover:text-[#071A36] dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-850'
                        }`}
                        id="tab-saas-admin"
                      >
                        <div className={`p-2 rounded-xl shrink-0 transition-colors ${
                          activeTab === 'saas_admin' ? 'bg-[#1C4F8A] text-white shadow-2xs' : 'bg-slate-200/60 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
                        }`}>
                          <Settings className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <span className={`block text-[11px] font-extrabold uppercase tracking-wide leading-none ${activeTab === 'saas_admin' ? 'text-[#071A36] dark:text-blue-100' : ''}`}>
                            ADMIN SAAS
                          </span>
                          <span className="block text-[9px] text-slate-400 mt-1 font-semibold truncate uppercase tracking-wider">
                            Suscripciones
                          </span>
                        </div>
                      </button>
                    )}

                  </nav>
                </div>

                {/* Switchable Active Module Tab Content with neat slide transition */}
                <div className="min-h-128" id="module-content-frame">
                  <AnimatePresence mode="wait">
                    {activeTab === 'registro_ordenes' && (
                      <motion.div
                        key="registro-tab-content"
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -15 }}
                        transition={{ duration: 0.18 }}
                      >
                        <RegistroOrdenes
                          ordenes={tenantFilteredOrdenes}
                          users={tenantFilteredUsers}
                          currentUser={currentUser}
                          onAddOrden={handleAddOrden}
                          onDeleteOrden={handleDeleteOrden}
                          onDeleteMultipleOrdenes={handleDeleteMultipleOrdenes}
                          onClearAllData={handleClearAllData}
                          onUpdateOrdenStatus={handleUpdateOrdenStatus}
                          isReadOnly={isReadOnlyMode()}
                        />
                      </motion.div>
                    )}

                    {activeTab === 'disposicion_productos' && (
                      <motion.div
                        key="disposicion-tab-content"
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -15 }}
                        transition={{ duration: 0.18 }}
                      >
                        <DisposicionProductos
                          ordenes={tenantFilteredOrdenes}
                          disposiciones={tenantFilteredDisposiciones}
                          currentUser={currentUser}
                          users={tenantFilteredUsers}
                          onAddDisposicion={handleAddDisposicion}
                          onUpdateOrdenStatus={handleUpdateOrdenStatus}
                          onUpdateDisposicion={handleUpdateDisposicion}
                          isReadOnly={isReadOnlyMode()}
                        />
                      </motion.div>
                    )}

                    {activeTab === 'dashboard_productividad' && (
                      <motion.div
                        key="dashboard-tab-content"
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -15 }}
                        transition={{ duration: 0.18 }}
                      >
                        <DashboardProductividad
                          ordenes={tenantFilteredOrdenes}
                          disposiciones={tenantFilteredDisposiciones}
                          currentUser={currentUser}
                        />
                      </motion.div>
                    )}

                    {activeTab === 'gestion_usuarios' && (
                      <motion.div
                        key="gestion-usuarios-tab-content"
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -15 }}
                        transition={{ duration: 0.18 }}
                      >
                        <GestionUsuarios
                          users={tenantFilteredUsers}
                          currentUser={currentUser}
                          onUpdateUserStatus={handleUpdateUserStatus}
                          onUpdateUserPassword={handleUpdateUserPassword}
                          onDeleteUser={handleDeleteUser}
                          onUserCreated={handleUserCreated}
                          allGlobalUsers={users}
                          onUpdateUserModules={handleUpdateUserModules}
                          onUpdateUserSignature={handleUpdateUserSignature}
                          isReadOnly={isReadOnlyMode()}
                        />
                      </motion.div>
                    )}

                    {activeTab === 'gestor_resguardo' && (
                      <motion.div
                        key="gestor-resguardo-tab-content"
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -15 }}
                        transition={{ duration: 0.18 }}
                      >
                        <GestorResguardoModule
                          currentUser={currentUser}
                          ordenes={tenantFilteredOrdenes}
                          disposiciones={tenantFilteredDisposiciones}
                          users={tenantFilteredUsers}
                          backups={backups}
                          onGenerateManualBackup={() => handleCreateBackup('MANUAL')}
                          onRestoreCompleted={handleRestoreCompleted}
                          onDeleteBackup={handleDeleteBackup}
                          onClearAllData={handleClearAllData}
                          isSyncing={isCloudSyncing}
                          onForceSync={handleManualCloudSync}
                          isReadOnly={isReadOnlyMode()}
                        />
                      </motion.div>
                    )}

                    {activeTab === 'saas_admin' && (
                      <motion.div
                        key="saas-admin-tab-content"
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -15 }}
                        transition={{ duration: 0.18 }}
                      >
                        <SaaSAdminPanel 
                          currentUser={currentUser} 
                          users={users}
                          onUpdateUserStatus={handleUpdateUserStatus}
                        />
                      </motion.div>
                    )}

                  </AnimatePresence>
                </div>
              </>
            )}

          </main>

          {/* Secure authenticated footer */}
          <footer className="py-6 border-t border-slate-200 mt-12 bg-white dark:bg-slate-900 transition-colors">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-slate-500 dark:text-slate-400 text-xs font-sans space-y-1.5">
              <div className="flex items-center justify-center space-x-2 text-xs font-black uppercase tracking-wider text-slate-700 dark:text-slate-200">
                <span>PHARMA <span className="text-teal-600 dark:text-teal-400">TRACE</span></span>
                <span className="text-slate-300 dark:text-slate-700">•</span>
                <span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium normal-case">Sistema Integral para Control, Trazabilidad y Gestión de Unidosis v5.0</span>
              </div>
              <p>© 2026 Quality Procesos y Servicios S.A.S. Todos los derechos reservados.</p>
              <div className="flex flex-wrap items-center justify-center gap-4 font-mono text-[10px] text-slate-400 dark:text-slate-500 pt-1">
                <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> Sesión activa: {currentUser.nombreCompleto} ({currentUser.cargo})</span>
                {sessionActiveCompany && (
                  <span className="flex items-center gap-1"><Building className="h-3 w-3 text-slate-400" /> Empresa: {sessionActiveCompany.nombreEmpresa} (Nit {sessionActiveCompany.nit})</span>
                )}
              </div>
            </div>
          </footer>
        </div>
      )}

      <BackupManagerModal
        isOpen={showBackupModal}
        onClose={() => setShowBackupModal(false)}
        backups={backups}
        currentUser={currentUser}
        onGenerateManualBackup={() => handleCreateBackup('MANUAL')}
        onRestoreCompleted={handleRestoreCompleted}
        onDeleteBackup={handleDeleteBackup}
      />

      {renewalCompanyData && (
        <RenewalWarningModal
          isOpen={showRenewalModal}
          onClose={() => {
            if (renewalCompanyData && currentUser) {
              const list = getSavedCompanies();
              const comp = list.find(c => c.id === (currentUser.companyId || 'company-insumedic')) || list[0];
              if (comp && comp.fechaVencimiento) {
                const dismissKey = `dismissed_renewal_${comp.id}_${comp.fechaVencimiento.slice(0, 10)}`;
                sessionStorage.setItem(dismissKey, 'true');
              }
            }
            setShowRenewalModal(false);
          }}
          fechaVencimiento={renewalCompanyData.fechaVencimiento}
          diasRestantes={renewalCompanyData.diasRestantes}
          nombreEmpresa={renewalCompanyData.nombreEmpresa}
        />
      )}

    </div>
  );
}
